/**
 * Utility function to check if a file is a HEIC/HEIF image
 * @param {string} fileName - The name of the file
 * @returns {boolean} - True if the file is HEIC/HEIF
 */
export const isHeicFile = (fileName) => {
  if (!fileName) return false;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'heic' || extension === 'heif';
};

/**
 * Convert HEIC/HEIF file to JPEG blob
 * @param {File|Blob} heicFile - The HEIC file to convert
 * @returns {Promise<Blob>} - The converted JPEG blob
 */
export const convertHeicToJpeg = async (heicFile) => {
  try {
    const { default: heic2any } = await import('heic2any');

    const convertedBlob = await heic2any({
      blob: heicFile,
      toType: 'image/jpeg',
      quality: 0.8, // Adjust quality as needed (0.1 to 1.0)
    });

    // heic2any can return an array if multiple images, but for single images it returns a blob
    return Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error);
    throw new Error('Failed to convert HEIC image');
  }
};

/**
 * Convert HEIC URL to displayable JPEG URL
 * @param {string} heicUrl - The URL of the HEIC image
 * @returns {Promise<string>} - The object URL of the converted JPEG
 */
export const convertHeicUrlToJpeg = async (heicUrl) => {
  try {
    // Fetch the HEIC file
    const response = await fetch(heicUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch HEIC image');
    }

    const heicBlob = await response.blob();
    const jpegBlob = await convertHeicToJpeg(heicBlob);

    // Create object URL for the converted JPEG
    return URL.createObjectURL(jpegBlob);
  } catch (error) {
    console.error('Error converting HEIC URL to JPEG:', error);
    throw error;
  }
};

/**
 * Get the appropriate image URL for display
 * If the file is HEIC/HEIF, convert it to JPEG, otherwise return the original URL
 * @param {string} imageUrl - The original image URL
 * @param {string} fileName - The file name to check extension
 * @returns {Promise<string>} - The displayable image URL
 */
export const getDisplayableImageUrl = async (imageUrl, fileName) => {
  if (isHeicFile(fileName)) {
    return await convertHeicUrlToJpeg(imageUrl);
  }
  return imageUrl;
};
